import React, { useState, useEffect } from "react";
import { Button, Tag, Space, Drawer, Row, Col, Input, Select, DatePicker, Badge } from "antd";
import type { TableColumnsType } from "antd";
import { FilterOutlined, PlusCircleOutlined, CalendarOutlined, DollarOutlined, UserOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Icon } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import RABillAside from "./raBillAside";
import { formatCurrency } from "../../utils/Utils";

const { Option } = Select;
const { RangePicker } = DatePicker;

// Mock eligible work orders for RA Bill creation
const mockEligibleWorkOrders = [
  {
    id: "wo1",
    woNumber: "WO/2025/URB/0012",
    contractor: "ABC Infra Ltd",
    site: "Urban Rise - Tower A",
    project: "Tower Construction Phase 2",
    section: "Basement & Ground Floor",
    woTotalValue: 5000000,
    cumulativeWorkValue: 3500000,
    cumulativeWorkDone: 70.0,
    previousRABillsValue: 2500000,
    availableForBilling: 1000000, // Cum Work Value - Prev RA Bills
    lastRABillDate: "2025-06-15",
    status: "In Execution",
    contractType: "Construction Work",
    payOnCompletion: false,
    softHold: false,
    hardHold: false,
    accountingPeriod: "Open"
  },
  {
    id: "wo2",
    woNumber: "WO/2025/URB/0015",
    contractor: "XYZ Constructions",
    site: "Urban Rise - Tower B",
    project: "Tower Construction Phase 2",
    section: "Foundation & Basement",
    woTotalValue: 4200000,
    cumulativeWorkValue: 2550000,
    cumulativeWorkDone: 60.71,
    previousRABillsValue: 1700000,
    availableForBilling: 850000,
    lastRABillDate: "2025-05-20",
    status: "In Execution",
    contractType: "Construction Work",
    payOnCompletion: false,
    softHold: true,
    hardHold: false,
    accountingPeriod: "Open"
  },
  {
    id: "wo3",
    woNumber: "WO/2025/URB/0018",
    contractor: "BuildTech Pvt Ltd",
    site: "Urban Rise - Tower C",
    project: "Tower Construction Phase 1",
    section: "Superstructure",
    woTotalValue: 6000000,
    cumulativeWorkValue: 2700000,
    cumulativeWorkDone: 45.0,
    previousRABillsValue: 1500000,
    availableForBilling: 1200000,
    lastRABillDate: "2025-04-30",
    status: "In Execution",
    contractType: "Construction Work",
    payOnCompletion: false,
    softHold: false,
    hardHold: false,
    accountingPeriod: "Open"
  }
];

const CreateRABills: React.FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [eligibleWorkOrders, setEligibleWorkOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [filters, setFilters] = useState({
    contractor: '',
    woNumber: '',
    site: '',
    project: '',
    dateRange: null as any,
    minAvailable: ''
  });

  useEffect(() => {
    loadEligibleWorkOrders();
  }, []);

  const loadEligibleWorkOrders = () => {
    setLoading(true);
    try {
      // Filter work orders that are eligible for RA Bill creation
      const eligibleWOs = mockEligibleWorkOrders.filter(wo => 
        wo.status === 'In Execution' && 
        wo.availableForBilling > 0 && 
        !wo.hardHold &&
        wo.accountingPeriod === 'Open'
      );
      setEligibleWorkOrders(eligibleWOs);
    } catch (error) {
      console.error("Error loading eligible work orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRABill = (woId: string) => {
    navigate(`${process.env.PUBLIC_URL}/ra-bills-create-detail/${woId}`);
  };

  const resetFilters = () => {
    setFilters({
      contractor: '',
      woNumber: '',
      site: '',
      project: '',
      dateRange: null,
      minAvailable: ''
    });
  };

  const applyFilters = () => {
    setDrawerVisible(false);
    // Filter logic would be implemented here
  };

  // KPI calculations
  const totalEligibleWOs = eligibleWorkOrders.length;
  const totalAvailableValue = eligibleWorkOrders.reduce((sum, wo) => sum + wo.availableForBilling, 0);
  const softHoldCount = eligibleWorkOrders.filter(wo => wo.softHold).length;
  const highValueCount = eligibleWorkOrders.filter(wo => wo.availableForBilling > 500000).length;

  const columns: TableColumnsType<any> = [
    {
      title: "WO #",
      dataIndex: "woNumber",
      key: "woNumber",
      width: 200,
      fixed: "left",
      render: (text: string, record: any) => (
        <div>
          <div className="fw-bold text-primary">{text}</div>
          <div className="text-muted small mt-1">
            {record.contractType}
          </div>
        </div>
      ),
    },
    {
      title: "Site/Project",
      key: "siteProject",
      width: 220,
      render: (_, record) => (
        <div>
          <div className="fw-bold">{record.site}</div>
          <div className="text-muted small">{record.project}</div>
          <div className="text-muted small">Section: {record.section}</div>
        </div>
      ),
    },
    {
      title: "Contractor",
      dataIndex: "contractor",
      key: "contractor",
      width: 180,
    },
    {
      title: "WO Total Value",
      dataIndex: "woTotalValue",
      key: "woTotalValue",
      width: 150,
      align: "right",
      render: (value) => (
        <div className="fw-bold">
          {formatCurrency(value,"INR")}
        </div>
      ),
    },
    {
      title: "Work Done %",
      dataIndex: "cumulativeWorkDone",
      key: "cumulativeWorkDone",
      width: 120,
      align: "center",
      render: (percentage) => {
        const color = percentage >= 75 ? "#52c41a" : percentage >= 50 ? "#1890ff" : percentage >= 25 ? "#faad14" : "#ff4d4f";
        return <Tag color={color}>{percentage.toFixed(1)}%</Tag>;
      },
    },
    {
      title: "Cumulative Work Value",
      dataIndex: "cumulativeWorkValue",
      key: "cumulativeWorkValue",
      width: 160,
      align: "right",
      render: (value) => (
        <div className="fw-bold text-success">
          {formatCurrency(value,"INR")}
        </div>
      ),
    },
    {
      title: "Previous RA Bills",
      dataIndex: "previousRABillsValue",
      key: "previousRABillsValue",
      width: 150,
      align: "right",
      render: (value) => (
        <div className="text-info">
          {formatCurrency(value,"INR")}
        </div>
      ),
    },
    {
      title: "Available for Billing",
      dataIndex: "availableForBilling",
      key: "availableForBilling",
      width: 160,
      align: "right",
      render: (value) => (
        <div className="fw-bold text-primary">
          {formatCurrency(value,"INR")}
        </div>
      ),
      sorter: (a, b) => a.availableForBilling - b.availableForBilling,
    },
    {
      title: "Last RA Bill",
      dataIndex: "lastRABillDate",
      key: "lastRABillDate",
      width: 130,
      render: (date) => date ? dayjs(date).format("DD-MMM-YYYY") : "---",
      sorter: (a, b) => dayjs(a.lastRABillDate).unix() - dayjs(b.lastRABillDate).unix(),
    },
    {
      title: "Flags",
      key: "flags",
      width: 120,
      render: (_, record) => (
        <div>
          {record.softHold && (
            <Tag color="orange" title="Soft Hold Applied">SOFT HOLD</Tag>
          )}
          {record.payOnCompletion && (
            <Tag color="blue" title="Pay on Completion">POC</Tag>
          )}
          {record.accountingPeriod !== 'Open' && (
            <Tag color="red" title="Period Closed">CLOSED</Tag>
          )}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <Space wrap>
          <Button
            type="primary"
            size="small"
            icon={<PlusCircleOutlined />}
            onClick={() => handleCreateRABill(record.id)}
            disabled={record.hardHold || record.accountingPeriod !== 'Open'}
          >
            Create RA Bill
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="ra-bills-create"
      title="Create RA Bills"
      subtitle="Select eligible Work Orders to create Running Account Bills"
      columns={columns}
      dataSource={eligibleWorkOrders}
      loading={loading}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <RABillAside 
            updateSm={setSm} 
            sm={sm} 
          />
        </div>
      }
      extra={{
        tableScrollX: 1800,
        showSearch: true,
        searchPlaceholder: "Search by WO#, Contractor, or Site...",
        kpiWidgets: [
          {
            title: "Eligible Work Orders",
            value: totalEligibleWOs,
            icon: <PlusCircleOutlined />,
            color: "#52c41a"
          },
          {
            title: "Total Available Value",
            value: formatCurrency(totalAvailableValue,"INR"),
            icon: <DollarOutlined />,
            color: "#1890ff"
          },
          {
            title: "High Value WOs",
            value: highValueCount,
            icon: <CalendarOutlined />,
            color: "#722ed1"
          },
          {
            title: "Soft Hold WOs",
            value: softHoldCount,
            icon: <ClockCircleOutlined />,
            color: "#faad14"
          }
        ],
        advancedFilter: {
          visible: drawerVisible,
          onClose: () => setDrawerVisible(false),
          onOpen: () => setDrawerVisible(true),
          content: (
            <div className="p-4">
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <label className="form-label">Contractor</label>
                  <Select 
                    style={{ width: '100%' }}
                    placeholder="Select Contractor"
                    value={filters.contractor}
                    onChange={(value) => setFilters({...filters, contractor: value})}
                  >
                    <Option value="ABC Infra Ltd">ABC Infra Ltd</Option>
                    <Option value="XYZ Constructions">XYZ Constructions</Option>
                    <Option value="BuildTech Pvt Ltd">BuildTech Pvt Ltd</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Work Order #</label>
                  <Input
                    placeholder="Enter WO Number"
                    value={filters.woNumber}
                    onChange={(e) => setFilters({...filters, woNumber: e.target.value})}
                    allowClear
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">Site</label>
                  <Select 
                    style={{ width: '100%' }}
                    placeholder="Select Site"
                    value={filters.site}
                    onChange={(value) => setFilters({...filters, site: value})}
                  >
                    <Option value="Urban Rise - Tower A">Urban Rise - Tower A</Option>
                    <Option value="Urban Rise - Tower B">Urban Rise - Tower B</Option>
                    <Option value="Urban Rise - Tower C">Urban Rise - Tower C</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Project</label>
                  <Select 
                    style={{ width: '100%' }}
                    placeholder="Select Project"
                    value={filters.project}
                    onChange={(value) => setFilters({...filters, project: value})}
                  >
                    <Option value="Tower Construction Phase 1">Tower Construction Phase 1</Option>
                    <Option value="Tower Construction Phase 2">Tower Construction Phase 2</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Last RA Bill Date Range</label>
                  <RangePicker 
                    style={{ width: '100%' }}
                    value={filters.dateRange}
                    onChange={(dates) => setFilters({...filters, dateRange: dates})}
                    placeholder={['From Date', 'To Date']}
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">Min Available Value</label>
                  <Input
                    placeholder="Enter minimum available value"
                    value={filters.minAvailable}
                    onChange={(e) => setFilters({...filters, minAvailable: e.target.value})}
                    allowClear
                  />
                </Col>
              </Row>
              <div className="mt-4 d-flex gap-2">
                <Button type="primary" onClick={applyFilters} style={{ flex: 1 }}>
                  Apply Filters
                </Button>
                <Button onClick={resetFilters} style={{ flex: 1 }}>
                  Reset
                </Button>
              </div>
            </div>
          )
        }
      }}
    />
  );
};

export default CreateRABills;